import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo, Wordmark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;

const Field = ({ label, children }) => (
  <div><label style={{ margin: "10px 0 5px" }}>{label}</label>{children}</div>
);

const Check = ({ ok, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--mono)", fontSize: 13,
    color: ok ? "var(--green)" : "var(--red)", marginRight: 16 }}>
    <b>{ok ? "✓" : "✕"}</b>{children}
  </span>
);

export default function Demo() {
  const navigate = useNavigate();
  const [model, setModel] = useState("risk_model@v3.2");
  const [subject, setSubject] = useState("customer-7F3A");
  const [decision, setDecision] = useState("APPROVE");
  const [conf, setConf] = useState(0.91);
  const [cert, setCert] = useState(null);
  const [vr, setVr] = useState(null);
  const [vault, setVault] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [busy, setBusy] = useState("");

  const attest = async () => {
    setBusy("attest"); setVr(null);
    try {
      const { data } = await axios.post(`${API}/attest`, {
        model_id: model, subject,
        output: { decision, confidence: Number(conf) },
      });
      setCert(data);
    } catch { /* */ } finally { setBusy(""); }
  };

  const verify = async (tamper = false) => {
    if (!cert) return;
    setBusy(tamper ? "tamper" : "verify");
    const c = tamper
      ? { ...cert, output: { ...cert.output, decision: cert.output.decision === "APPROVE" ? "REJECT" : "APPROVE" } }
      : cert;
    try {
      const { data } = await axios.post(`${API}/verify`, { cert: c });
      setVr({ ...data, tampered: tamper });
    } catch { /* */ } finally { setBusy(""); }
  };

  const loadVault = async () => {
    setBusy("vault");
    try {
      const [v, i] = await Promise.all([axios.get(`${API}/vault`), axios.get(`${API}/vault/integrity`)]);
      setVault(v.data); setIntegrity(i.data);
    } catch { /* */ } finally { setBusy(""); }
  };

  return (
    <div className="wrap" style={{ maxWidth: 940, paddingTop: 28, paddingBottom: 72 }}>
      <div className="brand" style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <Logo w={40} h={34} /> <Wordmark size={18} />
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--dim)", textAlign: "right" }}>
          <a href="/" style={{ color: "var(--dim)" }} data-testid="demo-back" onClick={(e) => { e.preventDefault(); navigate("/"); }}>← Back to site</a><br />
          API · live demo
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="eyebrow" style={{ marginTop: 24 }}>Decision Certificate · attest → verify → vault</div>
        <h1 style={{ fontSize: "clamp(26px,4vw,36px)", color: "var(--head)", margin: "12px 0 8px", letterSpacing: "-1px" }}>
          Run the whole loop in 60 seconds.</h1>
        <p className="lede">Issue an Ed25519-signed, chain-hashed certificate for an AI decision, then verify it with no trust in
          the operator. <b>Sandbox:</b> signing uses a software key and TEE/GPU quotes are simulated (labelled <code>simulated:true</code>);
          the certificate format is identical in production.</p>

        <div className="grid g2" style={{ marginTop: 20, alignItems: "start" }}>
          {/* STEP 1 */}
          <div className="card">
            <div className="idx">STEP 01 · ATTEST</div>
            <h3 style={{ margin: "6px 0 10px" }}>Issue a Decision Certificate</h3>
            <Field label="Model"><input data-testid="demo-model" value={model} onChange={(e) => setModel(e.target.value)} /></Field>
            <Field label="Subject"><input data-testid="demo-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
            <div className="two">
              <Field label="Decision">
                <select data-testid="demo-decision" value={decision} onChange={(e) => setDecision(e.target.value)}
                  style={{ width: "100%", background: "var(--ink)", border: "1px solid var(--line2)", borderRadius: 10, color: "var(--head)", padding: "12px 13px", fontSize: 14 }}>
                  <option>APPROVE</option><option>REJECT</option><option>REVIEW</option>
                </select>
              </Field>
              <Field label="Confidence"><input data-testid="demo-conf" type="number" step="0.01" min="0" max="1" value={conf} onChange={(e) => setConf(e.target.value)} /></Field>
            </div>
            <button className="btn" data-testid="demo-attest" style={{ marginTop: 14 }} disabled={busy === "attest"} onClick={attest}>
              {busy === "attest" ? <><span className="spin" /> Signing…</> : "Issue & sign certificate →"}
            </button>
          </div>

          {/* STEP 2 */}
          <div className="card">
            <div className="idx">STEP 02 · VERIFY</div>
            <h3 style={{ margin: "6px 0 10px" }}>Independently verify</h3>
            <p style={{ fontSize: 14, color: "var(--mut)", margin: 0 }}>Recompute the hashes, check the Ed25519 signature and the
              append-only chain link — publicly, with no API key.</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
              <button className="btn" data-testid="demo-verify" disabled={!cert || busy === "verify"} onClick={() => verify(false)}>
                {busy === "verify" ? <><span className="spin" /> Verifying…</> : "Verify certificate"}
              </button>
              <button className="btn ghost" data-testid="demo-tamper" disabled={!cert || busy === "tamper"} onClick={() => verify(true)}>
                Tamper & re-verify
              </button>
            </div>
            {vr && (
              <div className="find" data-testid="demo-verify-result" style={{ marginTop: 14, borderLeftColor: vr.authentic ? "var(--green)" : "var(--red)" }}>
                <div className="title" style={{ marginBottom: 8 }}>
                  {vr.authentic ? "Authentic ✓" : "Verification FAILED ✕"} {vr.tampered && !vr.authentic ? " — tamper detected" : ""}
                </div>
                <Check ok={vr.hash_match}>hash match</Check>
                <Check ok={vr.signature_valid}>signature valid</Check>
                <Check ok={vr.chain_intact}>chain intact</Check>
                <div className="kv" style={{ marginTop: 8 }}>{vr.model_id} · decision {vr.decision} · seq {vr.seq}</div>
              </div>
            )}
          </div>
        </div>

        {/* CERT JSON */}
        {cert && (
          <div style={{ marginTop: 18 }}>
            <div className="flowlabel" style={{ marginTop: 0 }}>Signed certificate · cert_hash {cert.cert_hash.slice(0, 16)}…</div>
            <div className="codewrap" style={{ marginTop: 8 }}>
              <pre data-testid="demo-cert" style={{ maxHeight: 340, overflow: "auto" }}>{JSON.stringify(cert, null, 2)}</pre>
            </div>
          </div>
        )}

        {/* STEP 3 — VAULT */}
        <div style={{ marginTop: 26 }}>
          <div className="flowlabel" style={{ marginTop: 0 }}>Step 03 · VeriVault — the append-only chain</div>
          <button className="btn ghost" data-testid="demo-vault" style={{ marginTop: 10 }} disabled={busy === "vault"} onClick={loadVault}>
            {busy === "vault" ? <><span className="spin" /> Loading…</> : "Load vault & check integrity"}
          </button>
          {integrity && (
            <div className="note" data-testid="demo-integrity" style={{ marginTop: 12 }}>
              <b style={{ color: integrity.intact ? "var(--green)" : "var(--red)" }}>
                {integrity.intact ? "Chain intact ✓" : "Chain broken ✕"}</b> — {integrity.links} link{integrity.links !== 1 ? "s" : ""} re-verified.
            </div>
          )}
          {vault && vault.certificates.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="leads">
                <thead><tr><th>Seq</th><th>Model</th><th>Decision</th><th>cert_hash</th><th>prev_hash</th></tr></thead>
                <tbody>
                  {vault.certificates.map((c) => (
                    <tr key={c.cert_id} style={{ cursor: "default" }}>
                      <td style={{ color: "var(--head)", fontWeight: 600 }}>{c.chain.seq}</td>
                      <td className="kv">{c.model_id}</td>
                      <td>{c.output?.decision}</td>
                      <td className="kv">{c.cert_hash.slice(0, 14)}…</td>
                      <td className="kv">{c.chain.prev_hash.slice(0, 14)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="foot-note">Sandbox demo · software-signed, simulated attestation quotes. Production swaps in a PKCS#11 / KMIP HSM
          and real Intel TDX / NVIDIA CC quotes; the certificate format is unchanged.</div>
      </motion.div>
    </div>
  );
}
